import React, {useEffect, useMemo, useState} from 'react'
import styled from 'styled-components'
import {GIFT_SHOP_TAB_HEIGHT} from 'consts'
import {ThemeColor} from 'themes'
import {useDefaultState, useDidUpdate} from 'utils'
import convertUnit from 'lib/unit'
import {Paragraph} from '../Paragraph'
import {TabViewProps} from './TabViewProps'

interface StyledTabItemContainerProps {
  isSelected: boolean
  disabledColor: ThemeColor
  bottomBorder: boolean
}

interface StyledTabItemContentContainerProps {
  isSelected: boolean
}

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  z-index: 1;
  background-color: ${({theme}) => theme.white_1};
`

const StyledTabContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: ${convertUnit(GIFT_SHOP_TAB_HEIGHT)};
`

const StyledTabIconContainer = styled.div`
  margin-right: 12px;
`

const StyledTabItemContainer = styled.div<StyledTabItemContainerProps>`
  ${({isSelected, theme, disabledColor, bottomBorder}) => ({
    borderBottomColor: isSelected
      ? theme.primary_5
      : bottomBorder
      ? theme[disabledColor]
      : undefined,
    color: isSelected ? theme.primary_5 : theme[disabledColor],
    borderBottomStyle: isSelected || bottomBorder ? 'solid' : undefined,
    borderBottomWidth: isSelected || bottomBorder ? convertUnit(3) : 0,
  })}
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: ${convertUnit(12)};
  :hover {
    cursor: pointer;
    opacity: 0.6;
  }
`

const StyledTitle = styled(Paragraph)`
  color: inherit;
  text-align: center;
`

const StyledTabItemContentContainer = styled.div<StyledTabItemContentContainerProps>`
  display: ${({isSelected}) => (isSelected ? 'flex' : 'none')};
  flex: 1;
`

export default function TabView<T extends string>({
  routes,
  selectedRoute,
  containerStyle,
  stateSelect,
  disabledColor = 'gray_1',
  bottomBorder = true,
  disabledClick = false,
  showTab = true,
  persistContent = false,
  renderIcon,
  renderTitle,
  renderItem,
  onClickItem,
}: TabViewProps<T>) {
  const [select, setSelect] = useDefaultState<T | undefined>(
    selectedRoute || (routes.length ? routes[0] : undefined),
    stateSelect,
  )
  const [visitedRoutes, setVisitedRoutes] = useState<Set<T>>(new Set())

  const handleRenderTabItems = useMemo(
    () =>
      routes.map((route) => (
        <StyledTabItemContainer
          data-testid="tab"
          key={route}
          isSelected={select === route}
          disabledColor={disabledColor}
          bottomBorder={bottomBorder}
          onClick={
            disabledClick
              ? undefined
              : () => {
                  setSelect(route)
                  onClickItem && onClickItem(route)
                }
          }>
          <StyledTabIconContainer>
            {renderIcon ? renderIcon(route) : null}
          </StyledTabIconContainer>
          {renderTitle ? (
            <StyledTitle fontSize="m" fontWeight="medium">
              {renderTitle(route)}
            </StyledTitle>
          ) : null}
        </StyledTabItemContainer>
      )),
    [
      routes,
      select,
      disabledColor,
      bottomBorder,
      disabledClick,
      renderIcon,
      renderTitle,
      setSelect,
      onClickItem,
    ],
  )

  const handleRenderItem = useMemo(() => {
    if (!select) return null

    if (persistContent) {
      return routes.map((route) => {
        if (!visitedRoutes.has(route)) return null
        return (
          <StyledTabItemContentContainer
            key={`tab-item-${route}`}
            isSelected={select === route}>
            {renderItem(route)}
          </StyledTabItemContentContainer>
        )
      })
    }
    return renderItem(select)
  }, [persistContent, renderItem, routes, select, visitedRoutes])

  useDidUpdate(() => setSelect(selectedRoute), [selectedRoute, setSelect])

  useEffect(() => {
    if (persistContent && select) {
      setVisitedRoutes((prev) => {
        if (prev.has(select)) {
          return prev
        }
        const newVisited = new Set(prev)
        newVisited.add(select)
        return newVisited
      })
    }
  }, [persistContent, select])

  return (
    <StyledContainer style={containerStyle}>
      {showTab && (
        <StyledTabContainer>{handleRenderTabItems}</StyledTabContainer>
      )}
      {handleRenderItem}
    </StyledContainer>
  )
}
